/**
 * Reading 500's opaque payloads.
 *
 * `call`, `contract` and `hand.extra` cross the wire as `unknown` — the core
 * must not know them — and the snapshot carries no rendered label for the
 * contract (the module's `contractLabel()` is server-side only). So this file
 * is the single tolerant reader: it accepts the shapes the 500 module is
 * specified to produce (ARCHITECTURE §2.5) and falls back to parsing the
 * human label the server already sends with every legal call.
 *
 * Everything here is *this game's* knowledge, which is why it lives under
 * games/five-hundred/ and not in table/.
 */
import type { Suit } from '@cards/shared'

export type Strain = Suit | 'NT'

export type Spec =
  | { readonly kind: 'suit'; readonly level: number; readonly strain: Strain }
  | { readonly kind: 'misere' }
  | { readonly kind: 'openMisere' }

/** Column order of the bid table printed on an Australian 500 scorepad. */
export const STRAINS: readonly Strain[] = ['S', 'C', 'D', 'H', 'NT']
export const LEVELS: readonly number[] = [6, 7, 8, 9, 10]

export const MISERE_VALUE = 250
export const OPEN_MISERE_VALUE = 500

/** 6♠ = 40, +20 per rung across then up. This is print on the pad, not a rule. */
export function ladderValue(level: number, strain: Strain): number {
  return 40 + 20 * ((level - 6) * 5 + STRAINS.indexOf(strain))
}

export const STRAIN_GLYPH: Record<Strain, string> = {
  S: '♠',
  C: '♣',
  D: '♦',
  H: '♥',
  NT: 'NT',
}
export const STRAIN_CLASS: Record<Strain, string> = {
  S: 's-s',
  C: 's-c',
  D: 's-d',
  H: 's-h',
  NT: '',
}
export const STRAIN_WORD: Record<Strain, string> = {
  S: 'spades',
  C: 'clubs',
  D: 'diamonds',
  H: 'hearts',
  NT: 'no trumps',
}

const isRecord = (x: unknown): x is Record<string, unknown> =>
  typeof x === 'object' && x !== null

function asStrain(x: unknown): Strain | null {
  if (typeof x !== 'string') return null
  const up = x.toUpperCase()
  if (up === 'NT' || up === 'NOTRUMP' || up === 'NOTRUMPS') return 'NT'
  if (up === 'S' || up === 'H' || up === 'D' || up === 'C') return up as Suit
  const glyph: Record<string, Strain> = { '♠': 'S', '♥': 'H', '♦': 'D', '♣': 'C' }
  return glyph[x] ?? null
}

/** `8♦`, `Misère`, `Open misère`, `pass` — the label the server already sends. */
export function parseSpecLabel(label: string): Spec | null {
  const t = label.trim().toLowerCase()
  if (t.startsWith('open mis')) return { kind: 'openMisere' }
  if (t.startsWith('mis')) return { kind: 'misere' }
  const m = /^(\d{1,2})\s*(nt|no ?trumps?|[♠♥♦♣shdc])/i.exec(label.trim())
  if (!m) return null
  const strain = asStrain(m[2])
  if (!strain) return null
  return { kind: 'suit', level: Number(m[1]), strain }
}

export function readSpec(x: unknown, depth = 0): Spec | null {
  if (depth > 4) return null
  if (typeof x === 'string') return parseSpecLabel(x)
  if (!isRecord(x)) return null

  const tag = String(x.kind ?? x.type ?? '').toLowerCase()
  if (tag === 'pass') return null
  if (tag === 'misere' || tag === 'misère') return { kind: 'misere' }
  if (tag === 'openmisere' || tag === 'open-misere' || tag === 'openmisère') {
    return { kind: 'openMisere' }
  }

  const level = typeof x.level === 'number' ? x.level : null
  const strain = asStrain(x.strain ?? x.suit ?? x.trump)
  if (level !== null && strain !== null) return { kind: 'suit', level, strain }

  for (const key of ['spec', 'bid', 'contract', 'call']) {
    if (key in x) {
      const nested = readSpec(x[key], depth + 1)
      if (nested) return nested
    }
  }
  return null
}

export function isPassCall(call: unknown, label?: string): boolean {
  if (isRecord(call)) {
    const tag = String(call.kind ?? call.type ?? '').toLowerCase()
    if (tag === 'pass') return true
    if (tag) return false
  }
  return (label ?? '').trim().toLowerCase() === 'pass'
}

export function specValue(spec: Spec): number {
  if (spec.kind === 'misere') return MISERE_VALUE
  if (spec.kind === 'openMisere') return OPEN_MISERE_VALUE
  return ladderValue(spec.level, spec.strain)
}

export function specLabel(spec: Spec): string {
  if (spec.kind === 'misere') return 'Misère'
  if (spec.kind === 'openMisere') return 'Open misère'
  return `${spec.level}${STRAIN_GLYPH[spec.strain]}`
}

/** Tricks the contract must take. Misère must take none. */
export function specTricks(spec: Spec): number {
  return spec.kind === 'suit' ? spec.level : 0
}

export interface ContractView {
  readonly spec: Spec
  readonly contractor: number | null
  readonly value: number
}

const SEAT_KEYS = ['contractor', 'declarer', 'bidder', 'winner', 'seat', 'by']

function findSeat(x: unknown, depth = 0): number | null {
  if (!isRecord(x) || depth > 3) return null
  for (const key of SEAT_KEYS) {
    const v = x[key]
    if (typeof v === 'number' && Number.isInteger(v) && v >= 0 && v < 8) return v
  }
  for (const key of ['spec', 'bid', 'contract', 'high']) {
    if (key in x) {
      const nested = findSeat(x[key], depth + 1)
      if (nested !== null) return nested
    }
  }
  return null
}

export function readContract(contract: unknown): ContractView | null {
  const spec = readSpec(contract)
  if (!spec) return null
  const explicit = isRecord(contract) && typeof contract.value === 'number' ? contract.value : null
  return { spec, contractor: findSeat(contract), value: explicit ?? specValue(spec) }
}

/** The auction strip needs each seat's last call; `FhAuctionState.calls` is the
 *  documented shape, but any `{seat, call}` list will do. */
export function readAuctionCalls(auction: unknown): { seat: number; call: unknown }[] {
  if (!isRecord(auction)) return []
  const raw = auction.calls
  if (!Array.isArray(raw)) return []
  const out: { seat: number; call: unknown }[] = []
  for (const entry of raw) {
    if (!isRecord(entry) || typeof entry.seat !== 'number') continue
    out.push({ seat: entry.seat, call: entry.call })
  }
  return out
}

/** Which suit the Joker was nominated to, when the module records it. */
export function readJokerSuit(extra: unknown): Suit | null {
  if (!isRecord(extra)) return null
  const v = extra.jokerNominated ?? extra.jokerSuit
  const s = asStrain(v)
  return s && s !== 'NT' ? s : null
}
